const BOM = String.fromCharCode(0xfeff);

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

/**
 * A spreadsheet treats a cell that starts with = + - or @ as a formula, and a comment or symbol typed by a user could then run
 * one when the file is opened. Such text cells get a leading apostrophe. Numbers are written as numbers (a negative profit
 * is not text), so only string values are guarded.
 */
export function safeCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** CSV with a header row, CRLF line ends (as spreadsheets expect) and a byte-order mark so accents and symbols survive. */
export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const lines = [columns.map((c) => safeCell(c.header)).join(',')];
  for (const row of rows) lines.push(columns.map((c) => safeCell(c.value(row))).join(','));
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** Hands a CSV to the user as a file. Not testable without a DOM download, so it is kept tiny. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
